import { useQuery } from "@tanstack/react-query";

import { queryClient } from "@/lib/query/queryClient";

import { PortalAPI } from "./api/portalApi";

export const PORTAL_KEY = ["company-portal"] as const;

export const portalKeys = {
  me: () => [...PORTAL_KEY, "me"] as const,
  home: () => [...PORTAL_KEY, "home"] as const,
  documents: (id: string) => [...PORTAL_KEY, "documents", id] as const,
  messages: (id: string) => [...PORTAL_KEY, "messages", id] as const,
  form: () => [...PORTAL_KEY, "form"] as const,
  impact: (costCenter: string, caseType: string) =>
    [...PORTAL_KEY, "impact", costCenter, caseType] as const,
  spend: () => [...PORTAL_KEY, "spend"] as const
};

export const refreshPortal = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: PORTAL_KEY });

export const usePortalMe = (enabled = true) =>
  useQuery(
    { queryKey: portalKeys.me(), queryFn: PortalAPI.me, enabled, retry: false },
    queryClient
  );

export const usePortalHome = () =>
  useQuery(
    { queryKey: portalKeys.home(), queryFn: PortalAPI.home },
    queryClient
  );

export const useEmployeeDocuments = (id: string | null, enabled: boolean) =>
  useQuery(
    {
      queryKey: portalKeys.documents(id ?? ""),
      queryFn: () => PortalAPI.documents(id ?? ""),
      enabled: Boolean(id) && enabled
    },
    queryClient
  );

export const useEmployeeMessages = (id: string | null, enabled: boolean) =>
  useQuery(
    {
      queryKey: portalKeys.messages(id ?? ""),
      queryFn: () => PortalAPI.messages(id ?? ""),
      enabled: Boolean(id) && enabled
    },
    queryClient
  );

export const useRequestForm = (enabled: boolean) =>
  useQuery(
    { queryKey: portalKeys.form(), queryFn: PortalAPI.requestForm, enabled },
    queryClient
  );

export const useBudgetImpact = (
  costCenter: string,
  caseType: string,
  enabled: boolean
) =>
  useQuery(
    {
      queryKey: portalKeys.impact(costCenter, caseType),
      queryFn: () => PortalAPI.budgetImpact(costCenter, caseType),
      enabled: enabled && Boolean(costCenter)
    },
    queryClient
  );

export const usePortalSpend = (enabled: boolean) =>
  useQuery(
    { queryKey: portalKeys.spend(), queryFn: PortalAPI.spend, enabled },
    queryClient
  );
